import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Users, Mail, Calculator, Search, Download, RefreshCw, 
  Loader2, Calendar, MapPin, Phone, ExternalLink, Filter,
  TrendingUp, Clock, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../utils/api';


var authAxios = axios.create();
authAxios.interceptors.request.use(function(config) {
  var token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = 'Bearer ' + token;
  }
  return config;
});

export default function AdminLeadsNewsletter() {
  var [activeSection, setActiveSection] = useState('leads');
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  
  // Calculator Leads state
  var [leads, setLeads] = useState([]);
  var [leadsTotal, setLeadsTotal] = useState(0);
  
  // Newsletter Subscribers state
  var [subscribers, setSubscribers] = useState([]);
  var [subscribersTotal, setSubscribersTotal] = useState(0);
  var [subscribersActive, setSubscribersActive] = useState(0);
  var [statusFilter, setStatusFilter] = useState('all');

  useEffect(function() {
    if (activeSection === 'leads') {
      fetchLeads();
    } else {
      fetchSubscribers();
    }
  }, [activeSection, statusFilter]);

  async function fetchLeads() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/calculator-leads?limit=200');
      setLeads(res.data.leads || []);
      setLeadsTotal(res.data.total || 0);
    } catch (e) {
      console.error('Error fetching leads:', e);
      toast.error('Error al cargar leads');
    }
    setLoading(false);
  }

  async function fetchSubscribers() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/newsletter/subscribers?status=' + statusFilter + '&limit=200');
      setSubscribers(res.data.subscribers || []);
      setSubscribersTotal(res.data.total || 0);
      setSubscribersActive(res.data.active || 0);
    } catch (e) {
      console.error('Error fetching subscribers:', e);
      toast.error('Error al cargar suscriptores');
    }
    setLoading(false);
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    var date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function formatCurrency(amount) {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0
    }).format(amount);
  }

  function exportToCSV(data, filename) {
    if (!data || data.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    var headers = Object.keys(data[0]);
    var csvContent = headers.join(',') + '\n';
    
    data.forEach(function(row) {
      var values = headers.map(function(header) {
        var val = row[header];
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') return JSON.stringify(val).replace(/,/g, ';');
        return String(val).replace(/,/g, ';');
      });
      csvContent += values.join(',') + '\n';
    });
    
    var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    var link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename + '_' + new Date().toISOString().split('T')[0] + '.csv';
    link.click();
    toast.success('Archivo exportado correctamente');
  }

  // Filter data by search term
  var filteredLeads = leads.filter(function(lead) {
    if (!searchTerm) return true;
    var search = searchTerm.toLowerCase();
    return (
      (lead.email && lead.email.toLowerCase().includes(search)) ||
      (lead.name && lead.name.toLowerCase().includes(search)) ||
      (lead.city && lead.city.toLowerCase().includes(search))
    );
  });

  var filteredSubscribers = subscribers.filter(function(sub) {
    if (!searchTerm) return true;
    var search = searchTerm.toLowerCase();
    return (
      (sub.email && sub.email.toLowerCase().includes(search)) ||
      (sub.name && sub.name.toLowerCase().includes(search))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-50">Leads y Suscriptores</h2>
            <p className="text-sm text-slate-400">Gestiona tus contactos y prospectos</p>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 bg-slate-800/50 p-1 rounded-lg w-fit">
        <Button
          variant={activeSection === 'leads' ? 'default' : 'ghost'}
          size="sm"
          onClick={function() { setActiveSection('leads'); setSearchTerm(''); }}
          className={activeSection === 'leads' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-slate-400 hover:text-slate-200'}
          data-testid="tab-leads"
        >
          <Calculator className="w-4 h-4 mr-2" />
          Leads Calculadora
          <Badge className="ml-2 bg-slate-700 text-slate-300">{leadsTotal}</Badge>
        </Button>
        <Button
          variant={activeSection === 'newsletter' ? 'default' : 'ghost'}
          size="sm"
          onClick={function() { setActiveSection('newsletter'); setSearchTerm(''); }}
          className={activeSection === 'newsletter' ? 'bg-purple-600 hover:bg-purple-700' : 'text-slate-400 hover:text-slate-200'}
          data-testid="tab-newsletter"
        >
          <Mail className="w-4 h-4 mr-2" />
          Newsletter
          <Badge className="ml-2 bg-slate-700 text-slate-300">{subscribersActive}</Badge>
        </Button>
      </div>

      {/* Search and Actions Bar */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por email, nombre..."
                value={searchTerm}
                onChange={function(e) { setSearchTerm(e.target.value); }}
                className="pl-10 bg-slate-800 border-slate-700 text-slate-100"
                data-testid="search-input"
              />
            </div>
            
            {activeSection === 'newsletter' && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={function(e) { setStatusFilter(e.target.value); }}
                  className="bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  data-testid="status-filter"
                >
                  <option value="all">Todos</option>
                  <option value="active">Activos</option>
                  <option value="unsubscribed">Desuscritos</option>
                </select>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={activeSection === 'leads' ? fetchLeads : fetchSubscribers}
                className="border-slate-700 text-slate-300"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={function() {
                  if (activeSection === 'leads') {
                    exportToCSV(filteredLeads, 'calculator_leads');
                  } else {
                    exportToCSV(filteredSubscribers, 'newsletter_subscribers');
                  }
                }}
                className="border-slate-700 text-slate-300"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculator Leads Section */}
      {activeSection === 'leads' && (
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">{leadsTotal}</p>
                    <p className="text-xs text-slate-400">Total Leads</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">
                      {leads.filter(function(l) { return l.newsletter_subscribed; }).length}
                    </p>
                    <p className="text-xs text-slate-400">Con Newsletter</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">
                      {leads.length > 0 ? formatCurrency(
                        leads.reduce(function(sum, l) { return sum + (l.estimated_cost || 0); }, 0) / leads.length
                      ) : '$0'}
                    </p>
                    <p className="text-xs text-slate-400">Costo Promedio</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">
                      {leads.filter(function(l) { 
                        var date = new Date(l.created_at);
                        var now = new Date();
                        return date.toDateString() === now.toDateString();
                      }).length}
                    </p>
                    <p className="text-xs text-slate-400">Hoy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Leads Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-slate-50 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-400" />
                Leads de la Calculadora ({filteredLeads.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="text-center p-12 text-slate-400">
                  <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay leads registrados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="leads-table">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Email</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Nombre</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Ciudad</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Costo Est.</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Tratamiento</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Newsletter</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredLeads.map(function(lead, idx) {
                        return (
                          <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-200 text-sm">{lead.email}</span>
                              </div>
                            </td>
                            <td className="p-4 text-slate-300 text-sm">{lead.name || '-'}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-300 text-sm">{lead.city || '-'}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-emerald-400 font-medium text-sm">
                                {formatCurrency(lead.estimated_cost)}
                              </span>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
                                {lead.treatment_type || '-'}
                              </Badge>
                            </td>
                            <td className="p-4">
                              {lead.newsletter_subscribed ? (
                                <CheckCircle className="w-5 h-5 text-emerald-400" />
                              ) : (
                                <XCircle className="w-5 h-5 text-slate-600" />
                              )}
                            </td>
                            <td className="p-4 text-slate-400 text-sm">
                              {formatDate(lead.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Newsletter Subscribers Section */}
      {activeSection === 'newsletter' && (
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">{subscribersTotal}</p>
                    <p className="text-xs text-slate-400">Total Suscriptores</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">{subscribersActive}</p>
                    <p className="text-xs text-slate-400">Activos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">
                      {subscribers.filter(function(s) { return s.subscriber_type === 'profesional'; }).length}
                    </p>
                    <p className="text-xs text-slate-400">Profesionales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-50">
                      {subscribers.filter(function(s) { return s.source === 'calculadora'; }).length}
                    </p>
                    <p className="text-xs text-slate-400">Desde Calculadora</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subscribers Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-slate-50 flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-400" />
                Suscriptores del Newsletter ({filteredSubscribers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : filteredSubscribers.length === 0 ? (
                <div className="text-center p-12 text-slate-400">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay suscriptores registrados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="subscribers-table">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Email</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Nombre</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Tipo</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Fuente</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Estado</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Fecha</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredSubscribers.map(function(sub, idx) {
                        return (
                          <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-200 text-sm">{sub.email}</span>
                              </div>
                            </td>
                            <td className="p-4 text-slate-300 text-sm">{sub.name || '-'}</td>
                            <td className="p-4">
                              <Badge 
                                className={
                                  sub.subscriber_type === 'profesional' 
                                    ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' 
                                    : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                }
                              >
                                {sub.subscriber_type === 'profesional' ? 'Profesional' : 'Persona'}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
                                {sub.source || 'web'}
                              </Badge>
                            </td>
                            <td className="p-4">
                              {sub.status === 'active' ? (
                                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Activo
                                </Badge>
                              ) : (
                                <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Desuscrito
                                </Badge>
                              )}
                            </td>
                            <td className="p-4 text-slate-400 text-sm">
                              {formatDate(sub.subscribed_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
